import React, { cloneElement } from 'react';

import PropTypes from 'prop-types';

function normalizeNodes(content: any) {
    if (!content) {
        return [];
    }

    return Array.isArray(content) ? content.filter(Boolean) : [content];
}

function ToolbarLayout({
    isMobile,
    className = '',
    actions = [],
    filterSlot = [],
    searchInput = null,
    searchButton = null,
    children,
}: any) {
    const actionItems = normalizeNodes(actions);
    const filterSlotItems = normalizeNodes(filterSlot);

    const containerClasses = [
        isMobile
            ? 'flex flex-col gap-3 rounded-2xl bg-white p-4'
            : 'flex w-full items-center gap-4 rounded-[26px] bg-white p-5 shadow-lg',
        className,
    ]
        .filter(Boolean)
        .join(' ');

    const enhanceMobileSearchInput = () => {
        if (!searchInput) {
            return null;
        }

        return cloneElement(searchInput, {
            variant: 'standard',
            InputProps: {
                ...(searchInput.props.InputProps || {}),
                disableUnderline: true,
                sx: {
                    // Reset MUI's default input padding first so a caller's
                    // explicit padding (e.g. paddingLeft) still applies.
                    fontSize: '0.875rem',
                    padding: 0,
                    ...(searchInput.props.InputProps?.sx || {}),
                },
            },
            sx: {
                ...(searchInput.props.sx || {}),
            },
        });
    };

    if (isMobile) {
        const inputEl = enhanceMobileSearchInput();

        return (
            <div
                className={containerClasses}
                style={{
                    width: 'calc(100% - 32px)',
                    // A floor, not a fixed height. Records/Pending stack one filter button and
                    // land on 184px anyway, but Realtime's filter slot holds the Active/Archive
                    // toggle *and* the group dropdown — under a fixed height the flex children
                    // shrank and the search row collapsed to its own border.
                    minHeight: '184px',
                    margin: '0 16px',
                    backgroundColor: '#FFFFFF',
                    boxShadow: '0px 4px 25px 0px #EAE9E9',
                }}
            >
                {actionItems.length > 0 && (
                    <div className='grid w-full grid-cols-2 items-stretch gap-3'>
                        {actionItems.map((action: any, index: any) => (
                            <div key={action?.key ?? index} className='flex w-full items-center justify-center'>
                                {action}
                            </div>
                        ))}
                    </div>
                )}

                {filterSlotItems.length > 0 && (
                    <div className='flex w-full items-stretch'>
                        {filterSlotItems.map((item: any, index: any) => (
                            <div key={item?.key ?? index} className='flex flex-1 items-center'>
                                <div className='flex-1'>{item}</div>
                            </div>
                        ))}
                    </div>
                )}

                {(inputEl || searchButton) && (
                    <div className='flex items-center justify-end overflow-hidden rounded-full border border-[#D4DCEB] bg-white'>
                        {inputEl && <div className='flex-1'>{inputEl}</div>}
                        {searchButton && <div className='px-4'>{searchButton}</div>}
                    </div>
                )}

                {children}
            </div>
        );
    }

    return (
        <div className={containerClasses}>
            {filterSlotItems.length > 0 && (
                <div className='flex items-center gap-3'>
                    {filterSlotItems.map((item: any, index: any) => (
                        <React.Fragment key={item?.key ?? index}>{item}</React.Fragment>
                    ))}
                </div>
            )}

            {(searchInput || searchButton) && (
                <div className='flex min-w-0 flex-1 items-center rounded-lg border border-slate-300 bg-white px-5 py-2 shadow-sm'>
                    {searchInput}
                    {searchButton}
                </div>
            )}

            {actionItems.length > 0 && (
                <div className='flex shrink-0 items-center gap-3 border-l border-slate-200 pl-6'>
                    {actionItems.map((action: any, index: any) => (
                        <React.Fragment key={action?.key ?? index}>{action}</React.Fragment>
                    ))}
                </div>
            )}

            {children}
        </div>
    );
}

ToolbarLayout.propTypes = {
    isMobile: PropTypes.bool.isRequired,
    className: PropTypes.string,
    actions: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]),
    filterSlot: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]),
    searchInput: PropTypes.node,
    searchButton: PropTypes.node,
    children: PropTypes.node,
};

export default ToolbarLayout;
